import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trophy, Medal, Flame, Star, Award, Heart, Zap, TrendingDown } from "lucide-react";

const badgeIcons = {
  "3_day_streak": { label: "3-Day Streak", icon: Flame, color: "text-orange-500" },
  "7_day_streak": { label: "7-Day Streak", icon: Flame, color: "text-red-500" },
  "14_day_streak": { label: "14-Day Streak", icon: Star, color: "text-yellow-500" },
  "21_day_streak": { label: "21-Day Streak", icon: Award, color: "text-purple-500" },
  "first_week": { label: "First Week", icon: Medal, color: "text-blue-500" },
};

const categories = [
  { key: "consistency", label: "Consistency", icon: Flame, color: "text-orange-500", desc: "Log 3 meals/day" },
  { key: "low_calorie", label: "Low Calorie", icon: TrendingDown, color: "text-blue-500", desc: "Healthiest avg per meal" },
  { key: "health_hero", label: "Health Hero", icon: Heart, color: "text-emerald-500", desc: "Healthiest meals" },
];

const CorporateLeaderboard = () => {
  const { token } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [data, setData] = useState(null);
  const [activeCategory, setActiveCategory] = useState("consistency");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await fetch(`${API}/corporate/programs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const d = await res.json();
          setPrograms(d.programs || []);
          if (d.programs?.length > 0) setSelectedProgram(d.programs[0].program_id);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchPrograms();
  }, [token]);

  useEffect(() => {
    if (!selectedProgram) return;
    const fetchLeaderboard = async () => {
      try {
        const res = await fetch(`${API}/corporate/programs/${selectedProgram}/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setData(await res.json());
      } catch (err) { console.error(err); }
    };
    fetchLeaderboard();
  }, [selectedProgram, token]);

  const getRankStyle = (rank) => {
    if (rank === 1) return "bg-yellow-50 border-yellow-300";
    if (rank === 2) return "bg-gray-50 border-gray-300";
    if (rank === 3) return "bg-amber-50 border-amber-300";
    return "bg-white border-emerald-100/50";
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-sm font-bold text-muted-foreground w-5 text-center">{rank}</span>;
  };

  const currentList = data ? (data[activeCategory] || []) : [];

  const renderMetric = (entry) => {
    if (activeCategory === "consistency") {
      return (
        <div className="flex items-center gap-6 text-sm">
          <div className="text-center">
            <p className="font-bold text-secondary flex items-center gap-1"><Zap className="w-4 h-4 text-orange-500" />{entry.perfect_days}</p>
            <p className="text-[10px] text-muted-foreground">Perfect Days</p>
          </div>
          <div className="text-center">
            <p className="font-bold text-secondary flex items-center gap-1"><Flame className="w-4 h-4 text-red-400" />{entry.streak_count}</p>
            <p className="text-[10px] text-muted-foreground">Streak</p>
          </div>
        </div>
      );
    }
    if (activeCategory === "low_calorie") {
      return (
        <div className="flex items-center gap-6 text-sm">
          <div className="text-center">
            <p className="font-bold text-secondary">{entry.avg_calories?.toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">Total kcal</p>
          </div>
          <div className="text-center">
            <p className="font-bold text-secondary">{entry.total_meals}</p>
            <p className="text-[10px] text-muted-foreground">Meals</p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-6 text-sm">
        <div className="text-center">
          <p className="font-bold text-secondary flex items-center gap-1"><Star className="w-4 h-4 text-emerald-500" />{entry.avg_health_score}</p>
          <p className="text-[10px] text-muted-foreground">Health Score</p>
        </div>
        <div className="text-center">
          <p className="font-bold text-secondary">{entry.total_meals}</p>
          <p className="text-[10px] text-muted-foreground">Meals</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-secondary">Leaderboard</h2>
          <p className="text-sm text-muted-foreground">Rankings by challenge alias — no personal data shown</p>
        </div>
        {programs.length > 0 && (
          <Select value={selectedProgram} onValueChange={setSelectedProgram}>
            <SelectTrigger className="w-[220px] rounded-xl" data-testid="leaderboard-program-select">
              <SelectValue placeholder="Select program" />
            </SelectTrigger>
            <SelectContent>
              {programs.map((p) => (
                <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2" data-testid="leaderboard-categories">
        {categories.map(({ key, label, icon: Icon, color, desc }) => (
          <button
            key={key}
            onClick={() => setActiveCategory(key)}
            className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
              activeCategory === key
                ? "border-emerald-500 bg-emerald-50"
                : "border-emerald-100/50 bg-white hover:border-emerald-200"
            }`}
            data-testid={`category-${key}`}
          >
            <Icon className={`w-5 h-5 ${activeCategory === key ? color : "text-muted-foreground"}`} />
            <span className={`text-xs font-semibold ${activeCategory === key ? "text-secondary" : "text-muted-foreground"}`}>{label}</span>
            <span className="text-[10px] text-muted-foreground">{desc}</span>
          </button>
        ))}
      </div>

      {/* Leaderboard List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => <div key={i} className="animate-pulse h-16 bg-white rounded-2xl" />)}
        </div>
      ) : currentList.length === 0 ? (
        <Card className="rounded-2xl p-8 text-center border-emerald-100">
          <Trophy className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
          <p className="text-muted-foreground">No data yet. Participants need to start logging meals!</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {currentList.map((entry, idx) => (
            <Card
              key={idx}
              className={`rounded-xl border p-4 ${getRankStyle(entry.rank)}`}
              data-testid={`leaderboard-rank-${entry.rank}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-8 flex justify-center">{getRankIcon(entry.rank)}</div>
                  <div>
                    <p className="font-semibold text-secondary">{entry.alias || `Participant #${entry.rank}`}</p>
                    {entry.badges?.length > 0 && (
                      <div className="flex items-center gap-2 mt-0.5">
                        {entry.badges.map((b) => {
                          const badge = badgeIcons[b];
                          if (!badge) return null;
                          const BadgeIcon = badge.icon;
                          return (
                            <span key={b} className={`inline-flex items-center gap-0.5 text-xs ${badge.color}`} title={badge.label}>
                              <BadgeIcon className="w-3 h-3" />
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {renderMetric(entry)}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CorporateLeaderboard;
